import {
    ArrowBack,
    DeleteForever,
    Edit,
    Notes,
    Person,
    PersonAddAlt1,
    Place,
    Print,
} from "@mui/icons-material";
import { useStyles } from "./styles";

import styled from "@emotion/styled";
import {
    Button,
    Chip,
    CircularProgress,
    IconButton,
    LinearProgress,
    emphasize,
} from "@mui/material";
import { useIsMutating } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppContext } from "../../../AppContext";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import Scrollbar from "../../../components/Scrollbar/Scrollbar";
import { SimpleSnackbarText } from "../../../components/SimpleSnackbar/SimpleSnackbar";
import { isRoleAdmin, isRoleService } from "../../../frappeConfig";
import { PosContext } from "../../../shared/PosContext";
import { useUpdateDoctypeData } from "../../../shared/hooks/useDocTypesData";
import { useDeleteOrder } from "../../../shared/hooks/useTableOrderItemsData";
import {
    getOrderShortName,
    parseAddress
} from "../../../utils/floorUtils";
import PrintOrderModal from "../../Tables/components/PrintOrderModal";
import tableIcon from "./../../../images/table-icons.svg";
import reservedTableIcon from "./../../../images/table-reserved-icon.svg";
import CustomerDialog from "./CustomerDialog/CustomerDialog";
import OrderCartItem from "./OrderCartItem";
import OrderDetailsDialog from "./OrderDetailsDialog";
import OrderNotesDialog from "./OrderNotesDialog";

const OrderCart = ({
    handleSelectOrder,
    handleCloseTable,
    handleSaveToTable,
    table,
    items,
    savedItems,
    selectedOrder,
    floor,
    minified = false,
    isOrderItemsLoading,
    isTakeaway,
    onItemEdit,
    onItemDelete,
    onSubmittedItemDelete,
    itemDeleting,
}) => {
    const classes = useStyles();

    const {
        customerContext,
        addressContext,
        addresses,
        orders,
        refetchCustomers,
        refetchAddresses,
        refetchTablesData,
    } = useContext(PosContext);

    const deleteOrderMutation = useDeleteOrder();

    const navigate = useNavigate();

    const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
    const toggleOrderDetails = () => setOrderDetailsOpen(!orderDetailsOpen);

    const [vat, setVat] = useState(0);
    const [subTotal, setSubTotal] = useState(0);
    const [total, setTotal] = useState(0);
    const [selectedCustomer, setSelectedCustomer] = customerContext;
    const [selectedAddress, setSelectedAddress] = addressContext;
    const { tableName, orderName } = useParams();
    const {
        userInfo,
        isTabsPos,
        serviceFee,
        elevatedPrivileges,
        restaurantSettings,
    } = useContext(AppContext);

    const isMutating = useIsMutating();

    const [printOrderModal, setPrintOrderModal] = useState(false);
    const togglePrintOrder = () => setPrintOrderModal(!printOrderModal);

    const [canSendToKitchen, setCanSendToKitchen] = useState(true);

    // Guard against accidental double-submit clicks around mutation success window
    const [sendButtonLocked, setSendButtonLocked] = useState(false);
    const postSuccessUnlockTimeoutRef = useRef(null);

    const [changeCustomerDialogOpen, setChangeCustomerDialogOpen] =
        useState(false);

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    const [orderNotesDialogOpen, setOrderNotesDialogOpen] = useState(false);

    const updateDoctype = useUpdateDoctypeData();

    useEffect(() => {
        if (!isTakeaway) {
            setSelectedCustomer(selectedOrder?.customer);
            setSelectedAddress(selectedOrder?.address);
        } else {
            if (selectedOrder?.customer) {
                setSelectedCustomer(selectedOrder?.customer);
                setSelectedAddress(selectedOrder?.address);
            }
        }
    }, [selectedOrder, isTakeaway, selectedOrder?.customer]);

    useEffect(() => {
        let addonsTotal = 0;
        let total = 0;
        items.filter(item => !item.item.parent_item).forEach((item) => {
            total += item.qty * (item.item.standard_rate || item.item.rate);

            if (item.addons) {
                item.addons.map((addon) => {
                    if (addon) {
                        addonsTotal +=
                            (addon.rate || addon.standard_rate) * item.qty;
                    }
                });
            }
        });

        savedItems.forEach((item) => {
            total += item.qty * (item.item.standard_rate || item.item.rate);

            if (item.addons) {
                item.addons.map((addon) => {
                    if (addon) {
                        addonsTotal +=
                            (addon.standard_rate || addon.rate) * item.qty || 0;
                    }
                });
            }
        });

        addonsTotal = addonsTotal || 0;

        if (serviceFee) {
            const subTotal = total + addonsTotal;
            const serviceFeeAmount = Number(
                Math.ceil(subTotal * (serviceFee / 100))
            );
            total = subTotal + serviceFeeAmount;
            if (selectedOrder?.discount > 0) {
                total = total - Math.ceil(selectedOrder?.discount);
            }
            setTotal(Math.ceil(total));
            setSubTotal(subTotal);
            setVat(serviceFeeAmount);
        } else {
            setTotal(total + addonsTotal);
        }

        setCanSendToKitchen(checkCanSendToKitchen());
    }, [items, savedItems, serviceFee]);

    const onSave = async () => {
        if (sendButtonLocked) return;
        setSendButtonLocked(true);
        if (isTakeaway && !selectedCustomer) {
            enqueueSnackbar(
                <SimpleSnackbarText label="Select a customer" /> //TODO FIX THIS :'_)
            );
            // Allow retry when validation fails
            setSendButtonLocked(false);
            return;
        }
        if (handleSaveToTable) {
            handleSaveToTable(table);
        }
    };

    // After mutation finishes, keep the button locked briefly to avoid double click
    useEffect(() => {
        if (sendButtonLocked && isMutating === 0) {
            if (postSuccessUnlockTimeoutRef.current) {
                clearTimeout(postSuccessUnlockTimeoutRef.current);
            }
            postSuccessUnlockTimeoutRef.current = setTimeout(() => {
                setSendButtonLocked(false);
                postSuccessUnlockTimeoutRef.current = null;
            }, 1200);
        }
        return () => {
            // cleanup on effect rerun
        };
    }, [isMutating, sendButtonLocked]);

    // If items were cleared or sending became no longer possible, unlock immediately
    useEffect(() => {
        if (!canSendToKitchen && sendButtonLocked) {
            setSendButtonLocked(false);
            if (postSuccessUnlockTimeoutRef.current) {
                clearTimeout(postSuccessUnlockTimeoutRef.current);
                postSuccessUnlockTimeoutRef.current = null;
            }
        }
    }, [canSendToKitchen, sendButtonLocked]);

    // When switching orders, reset the lock
    useEffect(() => {
        setSendButtonLocked(false);
        if (postSuccessUnlockTimeoutRef.current) {
            clearTimeout(postSuccessUnlockTimeoutRef.current);
            postSuccessUnlockTimeoutRef.current = null;
        }
    }, [selectedOrder?.name]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (postSuccessUnlockTimeoutRef.current) {
                clearTimeout(postSuccessUnlockTimeoutRef.current);
            }
        };
    }, []);

    const handleEditItem = (item) => {
        onItemEdit(item);
    };

    const handleDeleteItem = (item, props) => {
        onItemDelete(item, props);
    };

    const handleDeleteSubmittedItem = (item, password, qty, note, waste) => {
        onSubmittedItemDelete(item, password, qty, note, waste);
    };

    // Weather or not we have to send to kitchen.
    const checkCanSendToKitchen = () => {
        if (!items || items.length == 0) return false;

        for (const item of items) {
            if (item.item.status === "Attending") {
                return true;
            }
        }
        return false;
    };

    const handleCustomerChangeClose = () => {
        setChangeCustomerDialogOpen(false);
    };

    const assignCustomerToTable = (customerName, address) => {
        if (selectedOrder) {
            updateTableOrder(customerName, address);
        }
    };

    const handleDeleteOrder = () => {
        if (selectedOrder) {
            setDeleteConfirmOpen(true);
        }
    };

    const confirmDeleteOrder = (password, qty, notes, waste) => {
        if (selectedOrder) {
            deleteOrderMutation.mutate(
                { orderId: selectedOrder.name, masterPassword: password, notes: notes, waste: waste },
                {
                    onSuccess: () => {
                        refetchTablesData().then(() => {
                            enqueueSnackbar("Order deleted successfully", {
                                variant: "success",
                            });
                            navigate("/tables");
                        });
                    },
                    onError: (error) => {
                        console.log(error);
                    },
                }
            );
            setDeleteConfirmOpen(false);
        }
    };

    const cancelDeleteOrder = () => {
        setDeleteConfirmOpen(false);
    };

    const handleEditOrderNotes = () => {
        if (selectedOrder) {
            setOrderNotesDialogOpen(true);
        }
    };

    const handleSaveOrderNotes = (formData) => {
        if (selectedOrder) {
            updateDoctype.mutate(
                {
                    doctype: "Table Order",
                    name: selectedOrder.name,
                    formData: JSON.stringify(formData),
                },
                {
                    onSuccess: () => {
                        refetchTablesData();
                        setOrderNotesDialogOpen(false);
                    },
                }
            );
        }
    };

    const handleCloseOrderNotes = () => {
        setOrderNotesDialogOpen(false);
    };

    const onConfirmCustomerChanged = (customerName, address) => {
        refetchCustomers();
        refetchAddresses();
        setSelectedCustomer(customerName);
        if (address) {
            setSelectedAddress(address);
        }
        setChangeCustomerDialogOpen(false);
        assignCustomerToTable(customerName, address);
    };

    const onTableClose = () => {
        if ((items && items.length > 0) || savedItems.length > 0) {
            if (handleCloseTable) handleCloseTable(selectedOrder, table);
        }
    };

    const onSelectOrder = (order) => {
        if (handleSelectOrder) {
            handleSelectOrder(order);
        }
    };

    const closePos = () => {
        navigate("/tables");
    };

    const changeCustomer = (customer) => {
        setChangeCustomerDialogOpen(true);
    };

    const updateTableOrder = (customer, address) => {
        const payload = {
            customer,
        };

        if (address) {
            payload.address = address;
        }
        updateDoctype.mutate(
            {
                doctype: "Table Order",
                name: selectedOrder.name,
                formData: JSON.stringify(payload),
            },
            {
                onSuccess: () => {
                    refetchTablesData();
                    setChangeCustomerDialogOpen(false);
                },
            }
        );
    };

    const onCustomerSelected = (customer, address) => {
        setSelectedCustomer(customer);
        setSelectedAddress(address);

        if (selectedOrder) {
            if (address) {
                updateTableOrder(customer, address);
                // assignCustomerToTable(customer, address);
            } else {
                updateTableOrder(customer);
            }
            refetchTablesData();
        }

        setChangeCustomerDialogOpen(false);
    };

    const CustomerField = () => {
        const handleClick = () => {
            toggleOrderDetails();
        };

        if (!selectedAddress && selectedCustomer) {
            return (
                <div className="DetailsGroup">
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            handleClick();
                        }}
                    >
                        <Person className="DetailsIcon" />
                        {selectedCustomer}
                    </div>
                </div>
            );
        } else if (selectedAddress && selectedCustomer) {
            return (
                <div onClick={handleClick}>
                    <div className="DetailsGroup">
                        <Person className="DetailsIcon" /> {selectedCustomer}
                    </div>
                    <div className="DetailsGroup">
                        <Place className="DetailsIcon" />
                        {parseAddress(
                            addresses[selectedCustomer]?.find(
                                (i) => i.name === selectedAddress
                            )
                        )}
                    </div>
                </div>
            );
        } else {
            return "";
        }
    };

    return (
        <>
            <div className={classes.OrderWidget}>
                <div>
                    <div className={classes.TableDetails}>
                        {table.reserved ? (
                            <div className={classes.Table_ReservedWrap}>
                                <div>
                                    <img src={reservedTableIcon} />
                                </div>
                            </div>
                        ) : (
                            <div className="TableIcon">
                                {" "}
                                <img src={tableIcon} />
                            </div>
                        )}
                        <div className="TableName">
                            <div>
                                {isTabsPos ? `` : `${table.tableDescription}`}
                            </div>
                            <div>
                                {isTabsPos
                                    ? getOrderShortName(orderName) ||
                                      "New Order"
                                    : "Table details"}
                            </div>
                        </div>
                        <div style={{ marginRight: "1rem" }}>
                            <Button onClick={togglePrintOrder}>
                                <Print />
                            </Button>
                        </div>
                        <div className="Actions">
                            <IconButton onClick={() => closePos()}>
                                <ArrowBack fontSize="small" />
                            </IconButton>
                        </div>
                    </div>
                    <div className={classes.CustomerDetails}>
                        <div className="CustomerName">
                            <CustomerField />
                        </div>

                        {selectedOrder ? <IconButton
                            className={classes.Table_edit_customer}
                            onClick={() => handleEditOrderNotes()}
                        >
                            <Notes fontSize="x-small" color="white" />
                        </IconButton> : <></>}

                        <IconButton
                            className={classes.Table_edit_customer}
                            onClick={() => changeCustomer(selectedCustomer)}
                        >
                            <PersonAddAlt1 fontSize="x-small" color="white" />
                        </IconButton>

                        {elevatedPrivileges ||
                        isRoleAdmin(userInfo.role_profile_name) ? (
                            <IconButton
                                className={classes.Table_edit_customer}
                                onClick={() => handleDeleteOrder()}
                            >
                                <DeleteForever
                                    fontSize="x-small"
                                    color="white"
                                />
                            </IconButton>
                        ) : (
                            <></>
                        )}
                    </div>
                </div>

                {/* <div className={classes.OrdersNav}>
                    <Scrollbar>
                        {
                            // {!isTakeaway &&
                            orders &&
                                orders.map((order) => (
                                    <StyledBreadcrumb
                                        key={`Table_Order_${order.name}`}
                                        component="a"
                                        label={getOrderShortName(order.name)}
                                        icon={
                                            selectedOrder &&
                                            order.name ===
                                                (orderName ||
                                                    selectedOrder.name) ? (
                                                <Edit />
                                            ) : null
                                        }
                                        clickable
                                        onClick={() => onSelectOrder(order)}
                                    />
                                ))
                        }
                    </Scrollbar>
                </div> */}

                <div className={classes.OrderItems}>
                    <Scrollbar>
                        {items &&
                            items.map((item, index) => (
                                <OrderCartItem
                                    key={`OrderItem_${index}_${
                                        item.item.identifier || item.entry_name
                                    }`}
                                    onItemEdit={handleEditItem}
                                    onDelete={handleDeleteItem}
                                    item={item}
                                    itemDeleting={itemDeleting}
                                />
                            ))}{" "}
                        {savedItems && items.length > 0 && (
                            <div className={classes.OrderCartDivider}></div>
                        )}
                        {savedItems && !isOrderItemsLoading ? (
                            savedItems.map((item, index) => (
                                <OrderCartItem
                                    key={`OrderItem_${index}_${item.item.identifier}`}
                                    item={item}
                                    onDelete={handleDeleteSubmittedItem}
                                    isSaved={true}
                                    itemDeleting={itemDeleting}
                                />
                            ))
                        ) : (
                            <LinearProgress
                                className={classes.OrderItemsProgress}
                                style={{
                                    display: isOrderItemsLoading
                                        ? "block"
                                        : "none",
                                }}
                            ></LinearProgress>
                        )}
                        {isOrderItemsLoading && isTakeaway && (
                            <LinearProgress
                                className={classes.OrderItemsProgress}
                            ></LinearProgress>
                        )}
                    </Scrollbar>
                </div>

                <div className={classes.Pricing}>
                    {(serviceFee && !isTakeaway) ||
                    selectedOrder?.discount > 0 ? (
                        <div className="Pricing__Components">
                            <div>
                                <span>Subtotal</span>
                                <span>{subTotal}</span>
                            </div>{" "}
                            {serviceFee && !isTakeaway ? (
                                <div>
                                    <span>VAT</span>
                                    <span>+{vat}</span>
                                </div>
                            ) : (
                                <></>
                            )}
                            {selectedOrder?.discount > 0 ? (
                                <div>
                                    <span>Discount</span>
                                    <span>
                                        -{Math.ceil(selectedOrder?.discount)}
                                    </span>
                                </div>
                            ) : (
                                <></>
                            )}
                        </div>
                    ) : (
                        <div></div>
                    )}
                    <div className="Pricing__Total">
                        <div>
                            <span>Total</span>
                            <span>{isTakeaway ? subTotal : total}</span>
                        </div>
                    </div>
                </div>
                {minified ? (
                    <div className={classes.Actions}>
                        <button className="Btn--Primary" onClick={onTableClose}>
                            close table
                        </button>
                    </div>
                ) : (
                    <div className={classes.Actions}>
                        {canSendToKitchen ? (
                            <button
                                className="Btn--Primary"
                                onClick={onSave}
                                disabled={isMutating >= 1 || sendButtonLocked}
                            >
                                {isMutating ? (
                                    <CircularProgress className="CircularProgress" />
                                ) : (
                                    "Send to kitchen"
                                )}
                            </button>
                        ) : (
                            <div></div>
                        )}
                        {!isMutating &&
                            !isOrderItemsLoading &&
                            selectedOrder &&
                            !isRoleService(userInfo.role_profile_name) &&
                            items &&
                            savedItems?.length > 0 && (
                                <button
                                    className="Btn--Primary"
                                    onClick={
                                        restaurantSettings?.docs[0]
                                            ?.print_order_to_pay == 1
                                            ? selectedOrder?.invoice_printed ==
                                              1
                                                ? onTableClose
                                                : togglePrintOrder
                                            : onTableClose
                                    }
                                    disabled={isMutating === 1}
                                >
                                    {restaurantSettings?.docs[0]
                                        ?.print_order_to_pay == 1
                                        ? selectedOrder?.invoice_printed == 1
                                            ? "Pay"
                                            : "Print Order"
                                        : "Pay"}
                                </button>
                            )}
                    </div>
                )}

                <OrderDetailsDialog
                    isOpen={orderDetailsOpen}
                    selectedCustomer={selectedCustomer}
                    title={"Last Order Details"}
                    toggle={toggleOrderDetails}
                />

                <PrintOrderModal
                    open={printOrderModal}
                    onClose={() => {
                        refetchTablesData().then(() => {
                            navigate("/tables");
                        });
                        togglePrintOrder();
                    }}
                    orders={orders}
                    isTakeaway={isTakeaway}
                />

                <CustomerDialog
                    dialogOpen={changeCustomerDialogOpen}
                    onCancel={handleCustomerChangeClose}
                    onConfirm={onConfirmCustomerChanged}
                    selectedCustomer={selectedCustomer}
                    customerSelected={onCustomerSelected}
                ></CustomerDialog>

                <ConfirmationDialog
                    open={deleteConfirmOpen}
                    onConfirm={confirmDeleteOrder}
                    onClose={cancelDeleteOrder}
                    title="Delete Order"
                    message="Are you sure you want to delete this order? This action cannot be undone."
                    confirmText="Delete"
                    confirmColor="error"
                    loading={deleteOrderMutation.isLoading}
                    item={{ elevatedPrivileges }}
                    showWasteToggle={true}
                />

                <OrderNotesDialog
                    open={orderNotesDialogOpen}
                    onClose={handleCloseOrderNotes}
                    onSave={handleSaveOrderNotes}
                    selectedOrder={selectedOrder}
                    loading={updateDoctype.isLoading}
                />
            </div>
        </>
    );
};

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
    const backgroundColor =
        theme.palette.mode === "light"
            ? "#EFEFEF" // Custom light color
            : "#333333"; // Custom dark color
    return {
        backgroundColor,
        // color: theme.palette.text.primary,
        fontWeight: theme.typography.fontWeightRegular,
        "&:hover, &:focus": {
            backgroundColor: emphasize(backgroundColor, 0.06),
        },
        "&:active": {
            boxShadow: theme.shadows[1],
            backgroundColor: emphasize(backgroundColor, 0.12),
        },
        "& .CircularProgress": {
            width: "15px !important",
            height: "15px !important",
            color: "#e14954",
        },
        padding: "5px 12px",
        background: "#6E7187",
        borderRadius: "11.25px",
        fontSize: "8px",
        lineHeight: "10px",
        textAlign: "center",
        textTransform: "uppercase",
        color: "#FFFFFF",
    };
});

export default OrderCart;
